import { useDispatch, useSelector } from 'react-redux'
import { Pagination, SectionPanel, Spinner } from '@/components/ui'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import { useMemo } from 'react'

import { fetchBiens, setFilters, searchBiens } from '../../biens/biensSlice'
import BienItem from './BienItem'
import BienItemCard from './BienItemCard'
import ImmeubleItem from './ImmeubleItem'
import ImmeubleItemCard from './ImmeubleItemCard'
import BienSearch from './BienSearch'
import DecompteFiles from './DecompteFiles'

export default () => {
  const dispatch = useDispatch()
  const biens = useSelector((state) => state.biens.items)
  const filters = useSelector((state) => state.biens.filters)
  const pagination = useSelector((state) => state.biens.pagination)
  const isLoading = useSelector((state) => state.biens.loadingStatus.fetchList)

  // Séparer les biens en deux catégories
  const { biensNonImmeubles, immeubles } = useMemo(() => {
    const biensNonImmeubles = biens.filter(bien => bien.type_bien !== 'immeuble')
    const immeubles = biens.filter(bien => bien.type_bien === 'immeuble')
    return { biensNonImmeubles, immeubles }
  }, [biens])

  useUrlFilters({
    setFiltersAction: setFilters,
    fetchAction: (filters) => {
      if(filters.term) {
        return searchBiens(filters)
      } else {
        return fetchBiens(filters)
      }
    },
    filters,
    allowedParams: ['page', 'limit'],
  })

  const handlePageChange = (page) => {
    dispatch(setFilters({ page }))
  }  

  return (
    <div className="overflow-x-hidden">
    <SectionPanel>
      <SectionPanel.Title className="font-normal flex flex-col md:flex-row">
        <div className="mb-4">
          <h2 className="text-md md:text-xl text-center font-semibold mb-2">
            Détails de vos logements en gestion locative chez HPI
          </h2>
        </div>
        <div className="flex items-right justify-between items-center mb-4">
          <BienSearch />
        </div>
      </SectionPanel.Title>
      <SectionPanel.Body columns={1} bgColor={"white"} className='p-0'>
        {/* Biens non-immeubles */}
        {(!isLoading && biensNonImmeubles.length > 0) && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 px-4 pt-4">Logements</h3>
            
            {/* Affichage sur petits écrans */}
            <div className="block lg:hidden px-4">
              <div className="flex flex-wrap gap-4">
                {biensNonImmeubles.map((bien) => (
                  <BienItemCard key={bien.id} bien={bien} />
                ))}
              </div>
            </div>

            {/* Affichage sur écran > 768px en tableau */}
            <div className="hidden lg:block w-full overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Statut</th>
                    <th>Prix</th>
                    <th>Honoraires</th>
                    <th>GLI</th>
                    <th>Adresse</th>
                    <th>Type</th>
                    <th>Surface</th>
                    <th>Locataire</th>
                  </tr>
                </thead>
                <tbody>
                  {biensNonImmeubles.map((bien) => (
                    <BienItem key={bien.id} bien={bien} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Immeubles */}
        {(!isLoading && immeubles.length > 0) && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-4 px-4 pt-4">Immeubles</h3>
            
            {/* Affichage sur petits écrans */}
            <div className="block lg:hidden px-4">
              <div className="flex flex-wrap gap-4">
                {immeubles.map((bien) => (
                  <ImmeubleItemCard key={bien.id} bien={bien} />
                ))}
              </div>
            </div>

            {/* Affichage sur écran > 768px en tableau */}
            <div className="hidden lg:block w-full overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Statut</th>
                    <th>Prix</th>
                    <th>Honoraires</th>
                    <th>Adresse</th>
                    <th>Type</th>
                    <th>Surface</th>
                  </tr>
                </thead>
                <tbody>
                  {immeubles.map((bien) => (
                    <ImmeubleItem key={bien.id} bien={bien} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* États de chargement et messages d'erreur */}
        {isLoading && (
          <>
            {/* Affichage sur petits écrans */}
            <div className="block lg:hidden px-4 py-4">
              <Spinner color='primary'/>
            </div>

            {/* Affichage sur écran > 768px en tableau */}
            <div className="hidden lg:block w-full overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Statut</th>
                    <th>Prix</th>
                    <th>Honoraires</th>
                    <th>GLI</th>
                    <th>Adresse</th>
                    <th>Type</th>
                    <th>Surface</th>
                    <th>Locataire</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td colSpan="8"><Spinner color='primary'/></td></tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {!isLoading && biens.length === 0 && (
          <>
            {/* Affichage sur petits écrans */}
            <div className="block lg:hidden px-4 py-4 text-center text-gray-600 italic">
              Aucun bien trouvé
            </div>

            {/* Affichage sur écran > 768px en tableau */}
            <div className="hidden lg:block w-full overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Statut</th>
                    <th>Prix</th>
                    <th>Honoraires</th>
                    <th>GLI</th>
                    <th>Adresse</th>
                    <th>Type</th>
                    <th>Surface</th>
                    <th>Locataire</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-gray-600 italic">
                      Aucun bien trouvé
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination commune */}
        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        )}

        <div>
          <DecompteFiles />
        </div>
      </SectionPanel.Body>
    </SectionPanel>
  </div>
  )
}